import React, { useEffect, useRef, useState } from 'react'
import { useStateValue } from '@State'
import { client } from '@Utils'
import { ltiOrgKeyManagement as endpoints } from '@URLS'
import { LoadingSpinner, usePageRequest } from '@CommonComponents'
import { Pagination } from '@CommonComponents/buttons'
import { OrganizationAttributes } from './OrganizationAttributes'
import { OrgContextsTable } from './OrgContextsTable'

export const OrganizationDetails = ({ orgId }) => {
  const [currentState, dispatch] = useStateValue()
  const [isLoading, setIsLoading] = useState(false)
  const [fetchError, setFetchError] = useState(false)
  // Kept apart from selectedOrg, which each fetch clears, so the pager stays
  // mounted below the spinner while its page loads.
  const [contextCount, setContextCount] = useState(0)
  const {
    page: currentPage,
    attempt: pageAttempt,
    request: changePage,
    settle: settlePage,
  } = usePageRequest()
  const failureMessage = useRef(null)
  const resultsPerPage = 20
  useEffect(() => {
    // A later page or reload replaces this load, so its response is dropped.
    let cancelled = false
    const fetchData = async () => {
      setFetchError(false)
      dispatch({ type: 'selectOrg', payload: null })
      setIsLoading(true)
      try {
        let resp = await client.get(
          endpoints.getOrgDetails({ orgId: orgId, pageNum: currentPage })
        )
        if (cancelled) return
        dispatch({
          type: 'selectOrg',
          payload: resp.data.organization,
        })
        setContextCount(resp.data.organization.contextCount)
        settlePage()
      } catch (err) {
        if (cancelled) return
        settlePage(err)
        console.log(err)
        setFetchError(true)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    fetchData()
    return () => {
      cancelled = true
    }
  }, [currentState.reloadSwitch, pageAttempt])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const resp = await client.get(endpoints.getContacts())
        dispatch({
          type: 'setContacts',
          payload: resp.data.users,
        })
      } catch (err) {
        console.log(err)
      }
    }
    fetchData()
  }, [currentState.reloadSwitch])

  let component
  if (fetchError) {
    component = (
      <p className="text-danger" ref={failureMessage} tabIndex={-1}>
        Failed to load organization details. Please refresh and try again.
      </p>
    )
  } else if (isLoading || currentState.selectedOrg === null) {
    component = <LoadingSpinner outerClass="text-center py-4" />
  } else {
    component = (
      <React.Fragment>
        <OrganizationAttributes
          organization={currentState.selectedOrg}
          contacts={currentState.contacts}
        />
        <OrgContextsTable organization={currentState.selectedOrg} />
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      {component}
      {!fetchError && Math.ceil(contextCount / resultsPerPage) > 1 && (
        <Pagination
          currentPage={currentPage}
          rate={resultsPerPage}
          total={contextCount}
          setCurrentPage={changePage}
          failureMessage={failureMessage}
        />
      )}
    </React.Fragment>
  )
}
